/** @module csi/def/Context */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/resolver",
	"csi/middleware/rollup/Processor",
	"csi/sessionConstants",
	"csi/state",
	"csi/state/rootStore",
	"csi/xml"
], function(declare, lang, csi, _, html, locale, reflection, resolver, Processor, sessionConstants, state, {routerStore}, xml) {

	html.registerDataAttribute("DATA_CONTEXT_URLS", "context-urls");
	html.registerDataAttribute("DATA_CONTEXT_PARENT_ATTR", "context-parent-attr");
	html.registerDataAttribute("DATA_CONTEXT_PARENT_URL", "context-parent");
	html.registerDataAttribute("DATA_CONTEXT_READ_ONLY", "context-read-only");
	html.registerDataAttribute("DATA_CONTEXT_COLUMN_URL", "context-column");

	return declare(null, {
		// String: context parameter specifying current column url
		PARAM_COLUMN_URL: "paramColumnUrl",

		// String: context parameter specifying current row url
		PARAM_ROW_URL: "paramRowUrl",

		// Controller: reference to action controller if in context of action
		//ac: null,

		/** @property {number} availableHeight - available height to render content */
		//availableHeight: 0

		/** @property {number} availableWidth - available width to render content */
		//availableWidth: 0

		/** @property {boolean} bCaptureHtml - set to true when capturing resulting HTML rather than rendering to DOM */
		//bCaptureHtml: false

		/** @property {boolean} bCard - set to true to render attributes on cards (i.e. without red accent mark and without highlighting) */
		//bCard: false

		/** @property {boolean} bExcel - set to true when exporting to Excel */
		//bExcel: false

		// Boolean: indicate if browsing is allowed in current context
		//bForceBrowse: false

		/** @property {boolean} bNativeExport - set to true to render attribute values for native export rather than using normal formatting rules */
		//bNativeExport: false

		// Boolean: indicates if paths are already queried, in which case cache should not query for missing nodes while traversing paths
		// FIX: eventually this should always be true so that we do not need to do synchronous queries, but this is currently not always
		// true when adding new nodes to views
		//bPathsQueried: false,

		// Boolean: indicate if view in popup dialog
		//bPopup: false,

		// Boolean: indicate if printing
		//bPrint: false,

		// Boolean: indicate if view is read-only, in which case the context cannot be changed to not be read-only
		//bReadOnlyView: false,

		// Boolean: indicate if context is for reporting (nodes are not persistent)
		//bReporting: false,

		// Boolean: indicate if mouse click is reserved (i.e. do not allow other actions by mouse click)
		//bReserveClick: false,

		// Boolean: indicate if content should be completely static - no links, tooltips, actions
		//bStatic: false,

		// Boolean: indicate if currently in the context of an attribute tooltip
		//bTooltip: false,

		// Boolean: true if a request has hit an unreachable path.
		//pathUnreachable: false,

		// String: indicates which resolution image to use in view
		//prefImageSize: null,

		// Processor: rollup processor used to evaluate expressions
		//rp: null,

		// Cache: node cache for current context
		cache: null,

		// Object[]: array of objects containing current node context; each new path applied creates a new context
		contexts: null,

		// View: view definition (optional)
		defView: null,

		// FormData: form data for view (optional)
		fd: null,

		// csi.view.Widget: view containing context (optional)
		view: null,

		// String|Number: key for value when iterating through container attribute
		keyValue: null,

		// String: locale explicitly specified by view
		//locale: null,

		// parameter constants
		_READONLY: "readOnly",

		// Result: Passed into the getNewActions to be used in the hasAccess
		//queryResult: null,

		// Boolean: Whether to throw an exception when a path cannot be completed
		throwExceptionOnUnresolvedNode: false,


		constructor: function(/*csi.view.Widget*/view, /*Cache*/cache, /*String*/urlNode, /*String?*/urlParent, /*String?*/idRelation) {
			// summary: setup context for view
			this.bExcel = state.get("excel");
			this.bPrint = state.get("print");
			this.locale = locale.getLocale();
			this.prefImageSize = (view && view.getEffectiveImageSize()) || reflection.IMAGE_SIZE_MEDIUM;

			var readOnly = this.bPrint;

			if (view) {
				// initialize view-based parameters
				var viewPopup = view.getPopupView();

				this.bForceBrowse = view.bForceBrowse;
				this.bPopup = !!viewPopup;
				this.bStatic = !!view.isPreview;
				this.defView = view.defView;
				this.view = view;

				if (this.defView) {
					this.fd = this.defView.m_formData;
				}

				// context is read-only if the view is configured like so, or if view in popup dialog that is not editable
				readOnly = readOnly ||
					routerStore.locationIsDormant ||
					this.bStatic ||
					this.defView?.isReadOnly() ||
					(this.fd && !this.fd.m_bEditable) ||
					(viewPopup?.defView.m_formData && !viewPopup.defView.m_formData.m_bEditable);
			}

			this.cache = cache || resolver.m_cache;
			this.contexts = [{
				urlNode: urlNode,
				urlParent: urlParent ? urlParent : false,
				idRelation: idRelation,
				pushCount: 0,
				readOnly: !!readOnly
			}];

			// track original readOnly status - in some cases code will attempt to reset readOnly status to override default behavior, but
			// if view is read-only, then overriding should not be allowed
			this.bReadOnlyView = !!readOnly;
		},

		/**
		 * Return true if actions are allowed in this context
		 * @param {boolean} [bAllowReporting] - ignore whether or not in report mode; normally actions restricted when reporting
		*/
		allowActions(bAllowReporting) {
			const {bPrint, bReporting, bStatic} = this;
			return !bStatic && !bPrint && (bAllowReporting || !bReporting);
		},

		applyActionContext: function(/*DOMNode*/refNode, /*String?*/idAction) {
			// summary: apply current context to given node - only output parent context if parent
			// is already known or if the relation from parent to child is present; otherwise, don't
			// resolve parent if don't need to
			var c = this.contexts[0];
			if (c.idRelation || c.urlParent) {
				html.setDataAttribute(refNode, html.DATA_CONTEXT_PARENT_ATTR, c.idRelation);
				html.setDataAttribute(refNode, html.DATA_CONTEXT_PARENT_URL, this.getParentURL());
			}

			// FIX: not sure if empty string should be applied or not, but staying consistent with old code for now
			if (idAction != null) {
				html.setElementAction(refNode, idAction);
			}

			html.setElementContext(refNode, this.getNodeURL());
		},

		applyNodeContext: function(/*DOMNode*/refNode, /*Boolean*/bCheckPrevious) {
			// summary: apply current context to given DOM node
			var url = this.getNodeURL();

			if (bCheckPrevious) {
				var a = this.contexts;
				if (a.length > 1) {
					var urlLast = url;
					var urls = [];
					var obj;
					var i = 1;

					while (obj = a[i++]) {
						if ((obj.urlNode != urlLast) && (obj.urlNode != csi.EMPTY_URL)) {
							urls.push(obj.urlNode);
						}
						urlLast = obj.urlNode;
					}

					if (urls.length) {
						html.setDataAttribute(refNode, html.DATA_CONTEXT_URLS, urls.join());
					}
				}
			}

			html.setElementContext(refNode, url);
			html.setDataAttribute(refNode, html.DATA_CONTEXT_READ_ONLY, this.isReadOnly());
			if (!xml.isValidURL(url)) {
				url = this.getParentURL();

				if (xml.isValidURL(url)) {
					html.setDataAttribute(refNode, html.DATA_CONTEXT_PARENT_URL, url);
				}
			}
		},

		readNodeContext: function(/*DOMNode*/refNode) {
			// summary: fill context from DOM node
			const columnUrl = html.getDataAttribute(refNode, html.DATA_CONTEXT_COLUMN_URL);
			var urls = html.getDataAttribute(refNode, html.DATA_CONTEXT_URLS);
			if (urls) {
				// populate old contexts in reverse order
				var a = urls.split(",");

				for (var i = a.length - 1; i >= 0; --i) {
					this.setNodeURL(a[i]);
					this.pushParams();
				}
			}

			var url = html.getElementContext(refNode);
			this.setNodeURL(url);

			const isReadOnly = html.getDataAttribute(refNode, html.DATA_CONTEXT_READ_ONLY);
			this.setReadOnly(isReadOnly === "true");

			if (!xml.isValidURL(url)) {
				url = html.getDataAttribute(refNode, html.DATA_CONTEXT_PARENT_URL);

				if (xml.isValidURL(url)) {
					this.contexts[0].urlParent = url;
				}
			}

			this.setParam(this.PARAM_COLUMN_URL, columnUrl);
			return this;
		},

		canLink: function() {
			// summary: return if context allows links
			const {bForceBrowse, bPopup} = this;

			if (this.isStaticOutput()) {
				return false;
			}

			return (bForceBrowse != null) ? bForceBrowse : !bPopup;
		},

		/**
		 * Return true if attribute comments should be rendered
		 * @param {boolean} [bAllowTooltip] - ignore whether or not in tooltip mode
		 */
		canRenderComments(bAllowTooltip) {
			const {bCard, bNativeExport, bStatic, bTooltip} = this;
			return !bCard && !bStatic && !bNativeExport && !sessionConstants.isPortalUser && (bAllowTooltip || !bTooltip);
		},

		copyContext: function() {
			// summary: return new context based on current context
			var ctx = new this.constructor(this.view, this.cache, this.getNodeURL(), this.getParentURL(), this.getRelationId());
			ctx.ac = this.ac;
			ctx.availableHeight = this.availableHeight;
			ctx.availableWidth = this.availableWidth;
			ctx.fd = this.fd;
			ctx.bCaptureHtml = this.bCaptureHtml;
			ctx.bCard = this.bCard;
			ctx.bReporting = this.bReporting;
			ctx.bReserveClick = this.bReserveClick;
			ctx.bTooltip = this.bTooltip;
			ctx.prefImageSize = this.prefImageSize;
			ctx.keyValue = this.keyValue;
			return ctx;
		},

		forEach: function(/*String|Node[]*/nodes, /*Function*/f, /*Scope?*/scope) {
			// summary: call given function with each node in context
			var n = nodes && nodes.length;
			if (!n) return;

			var i;

			if (_.isString(nodes[0])) {
				nodes = _.map(nodes, function(url) {
					return this.cache.get(url);
				}, this);
			}

			this.pushParams();

			for (i = 0; i < n; ++i) {
				// FIX - not sure whether to pass true or not here?
				this.setNode(nodes[i], true);
				f.call(scope, this, i, nodes);
			}

			this.pop();
		},

		forEachChild: function(/*Function*/f, /*Any*/scope) {
			// summary: iterate over children in context
			this.forEach(this.getChildren(), f, scope);
		},

		/**
		 * Return node referenced by given attribute from node in context
		 * @param {string} attributeId
		 * @return {Node}
		 */
		getChildNode: function(attributeId) {
			const node = this.getContextNode();
			if (node) {
				return this.cache.get(node[attributeId]);
			}

			return null;
		},

		/**
		 * Return column node in context
		 * @return {C8Node | undefined}
		 */
		getColumnNode() {
			return this.getParamNode(this.PARAM_COLUMN_URL);
		},

		getNodeClass: function() {
			// summary: return reflection class for node in context
			return reflection.getNodeClass(this.getNode());
		},

		getNodeContext: function() {
			// summary: return node from previous context
			return this.cache.get(this.getNodeContextURL());
		},

		getNodeContextURL: function() {
			// summary: return url from previous context (if any)
			var c = this.contexts;
			var n = (c.length == 1) ? 0 : 1;
			return c[n].urlNode;
		},

		getNode: function() {
			// summary: return node from latest context
			return this.cache.get(this.contexts[0].urlNode);
		},

		getNodeOfType: function(/*String*/type) {
			// summary: traverse contexts to find node of given type
			var c = this.cache;
			var i = 0;
			var n;
			var obj;

			while (obj = this.contexts[i++]) {
				if (n = c.get(obj.urlNode)) {
					if (n.$Type == type) {
						return n;
					}
				}
			}

			return null;
		},

		getNodeType: function() {
			// summary: return node type from latest context
			var node = this.getNode();
			return node && node.$Type;
		},

		getNodeURL: function() {
			// summary: return url from latest context
			return this.contexts[0].urlNode;
		},

		/**
		 * Return row node in context
		 * @return {C8Node | undefined}
		 */
		getRowNode() {
			return this.getParamNode(this.PARAM_ROW_URL);
		},

		/**
		 * Return true if context is being used for static output
		 * @return {boolean}
		 */
		isStaticOutput() {
			const {bExcel, bNativeExport, bPrint, bReporting, bStatic} = this;
			return bExcel || bNativeExport || bPrint || bReporting || bStatic;
		},

		setNode: function(/*Node*/node, /*Boolean*/bSameParent) {
			// summary: set node for context
			var url = node ? node.$URL : null;
			
			if (!this.cache) {
				this.cache = resolver.m_cache;
			}

			// add node to cache if not already there
			if (node && !this.cache.get(url)) {
				this.cache.cacheNode(node);
			}

			if (node && bSameParent) {
				var c = this.contexts[0];

				// keep same parent context if node does not have an actual parent (global)
				if (!xml.isValidURL(node.__Parent__)) {
					// restore original parent
					if (c.urlParentOrig) {
						c.urlParent = c.urlParentOrig;
						c.idRelation = c.idRelationOrig;
					}
				} else if (!c.urlParentOrig) {
					// if parent is valid, then it will be used for new node, but store original in case need it for node without a parent
					c.urlParentOrig = c.urlParent;
					c.idRelationOrig = c.idRelation;
				}
				c.urlNode = url;
			} else {
				// set node and default to use real parent
				this.setNodeURL(url);
			}
			return this;
		},

		setNodeURL: function(/*String*/url) {
			// summary: reset url for latest context - used to enter selected nodes into context for access checks
			var c = this.contexts[0];
			c.urlNode = url || csi.EMPTY_URL;

			if (c.idRelation) {
				// if changing url, then make sure parent and relation are still valid
				var bFound = false;
				var value = xml.nodeGetAttributeValue(this.cache.get(c.urlParent), c.idRelation);
				if (value) {
					if (value instanceof Array) {
						bFound = (_.indexOf(value, url) >= 0);
					} else {
						bFound = csi.objectHasValue(value, url);
					}
				}

				if (!bFound && xml.isValidURL(url)) {
					c.urlParent = false;
					c.idRelation = null;
				}
			}

			return this;
		},

		getChildren: function(/*Boolean?*/bForce) {
			// summary: get child nodes of parent
			return this.cache.getChildren(this.getParent(), this.getRelationId(), bForce);
		},

		getContextNode: function() {
			// summary: by default if node not in context, then use parent
			return this.getNode() || this.getParent();
		},

		getNodeParent: function(/*Node?*/node, /*Boolean?*/bForce) {
			// summary: the context parent could be different than the node's actual composite parent
			const {cache} = this;
			const url = this.getNodeParentURL(node);
			let parent = cache.get(url);

			// allow Context to work with old (csi/store/Cache) and new caches (csi/state/Cache)
			if (bForce && cache.getNode) {
				parent = cache.getNode(url, true);
			}

			return parent;
		},

		getNodeParentURL: function(/*Node?*/node) {
			// summary: the context parent could be different than the node's actual composite parent
			if (node = node || this.getNode()) {
				return node.__Parent__;
			}

			return null;
		},

		getParent: function(/*Boolean?*/bForce) {
			// summary: return parent node from latest context
			const {cache} = this;
			const url = this.getParentURL();
			let parent = cache.get(url);

			// allow Context to work with old (csi/store/Cache) and new caches (csi/state/Cache)
			if (bForce && cache.getNode) {
				parent = cache.getNode(url, true);
			}

			return parent;
		},

		getParentURL: function() {
			// summary: return url from latest context
			var c = this.contexts[0];

			if (c.urlParent === false) {
				var node = this.getNode();
				if (node) {
					// find composite parent of current node
					var nodeParent = resolver.resolveParent(node.$URL, this.cache);
					if (nodeParent) {
						c.urlParent = nodeParent.$URL;

						var rcr = reflection.getRelationBetweenNodes(node, nodeParent, true);
						if (rcr) {
							c.idRelation = rcr.getId();
						} else if (!xml.isClientUrl(node.$URL)) {
							// ignore warning when node is a client URL; most likely this case arises from a Form view when creating a new node -
							// when we hook the client node up to the parent, we do not populate the new URL in the parent's composite relation
							console.warn("csi/def/Context - error resolving composite relation from " + nodeParent.$URL + " to " + node.$URL);
						}
					} else if (this.contexts.length > 1) {
						// if path applied and has no parent, then use parent node provided for view
						c.urlParent = this.contexts[this.contexts.length - 1].urlParent;
					}
				}

				if (!c.urlParent) {
					// reset so this check won't happen again
					c.urlParent = csi.EMPTY_URL;
				}
			}

			return c.urlParent;
		},

		getNodeAttributeValue: function(/*String*/id, /*Boolean*/bRollup) {
			// summary: get the value of attribute of the current context node.
			var node = this.getNode();
			if (!node) return null;

			if (bRollup && this.rp) {
				return this.rp.evaluateAttribute(id, node);
			}

			return node[id];
		},

		getParam: function(/*String*/param, /*Number?*/nStart) {
			// summary: get value of parameter on context
			var obj, p, i = nStart || 0;
			while (obj = this.contexts[i++]) {
				p = obj[param];
				if (p !== undefined) return p;
			}

			return null;
		},

		getParamNode: function(/*String*/param) {
			// summary: return node pointed to by param
			return this.cache.get(this.getParam(param));
		},

		setParam: function(/*String*/param, /*Any*/value) {
			// summary: set extra parameter on context
			(this.contexts[0])[param] = value;
			return this;
		},

		getRelationId: function() {
			// summary: return attribute id for relationship from nodeParent to node in latest context
			return xml.isValidURL(this.getParentURL()) ? this.contexts[0].idRelation : null;
		},

		getRelationType: function() {
			// summary: return type of relation attribute
			return xml.nodeAttributeGetType(this.getParent(), this.getRelationId());
		},

		getRelationValue: function() {
			// summary: return value of relation attribute
			var p = this.getParent();
			return p ? p[this.contexts[0].idRelation] : null;
		},

		hasContainerKey: function() {
			// summary: return if context has key for container attribute
			return this.keyValue != null;
		},

		hasNodeRight: function(/*String*/right) {
			// summary: return if user has access to edit node in context
			return xml.nodeHasRight(this.getNode(), right);
		},

		hasNodeAccess: function(/*String*/right, /*String?*/id) {
			// summary: return if user has access to edit node in context
			return xml.nodeHasAccess(this.getNode(), right, id);
		},

		hasParentAccess: function(/*String*/right) {
			// summary: return if user has access to edit node in context
			if (xml.rightIsMore(right, xml.RIGHT_READ) && (this.bPrint || this.isReadOnly())) {
				return false;
			}

			var parent = this.getParent();
			var id = this.getRelationId();
			return parent && id && xml.nodeHasAccess(parent, right, id);
		},

		setReadOnly: function(/*Boolean*/b) {
			// summary: set current context to read-only; do not allow changing to false if view is set to read-only
			if (this.bReadOnlyView && !b) {
				return;
			}

			this.setParam(this._READONLY, b);
		},

		isReadOnly: function(/*Boolean?*/bOriginal) {
			// summary: determine if current context allows actions
			return bOriginal ? this.bReadOnlyView : this.getParam(this._READONLY);
		},

		preRollup: function() {
			// summary: initialize rollup processor
			if (!this.bNativeExport) {
				this.rp = new Processor(this.getNode(), this.cache, this);
			}
		},

		postRollup: function() {
			// summary: store rollup dependencies on view
			if (this.rp && this.view) {
				this.view._rollupDependencies = lang.mixin(this.view._rollupDependencies || {}, this.rp.getDependencies() || {});
			}
		},

		pushParams: function(/*Object?*/params) {
			// summary: push new context with given params
			var c = this.contexts[0];
			var obj = {
				urlNode: c.urlNode,
				urlParent: c.urlParent,
				idRelation: c.idRelation,
				pushCount: 0
			};

			this.contexts.unshift(params ? lang.mixin(obj, params) : obj);
			return this;
		},

		push: function(/*String*/path) {
			// summary: calculate new context based on path
			var self = this;
			var bReadOnly = this.isReadOnly();
			var bForcePath = !this.bPathsQueried;
			var cache = this.cache;
			var nodeStart = this.getNode();
			var node = nodeStart;

			function onStep(nodeNext, nodePrevious, idRelation, bChild) {
				if (!nodeNext && !bForcePath && bChild) {
					// if next node in path is null, then it must not be visible to user because of access control, so mark as unresolved;
					// if forcing path, this will be done already as part of call to addNode
					self.testForUnresolvedUrl(nodePrevious[idRelation]);
				}

				if (!bReadOnly) {
					bReadOnly = !bChild || !reflection.isRelationEditable(nodePrevious, idRelation, nodeNext, nodeStart, self);
				}
			}

			// apply path to context - along the way determine if path is readOnly or not
			// based on whether or not it passes through aggregate relationship unless it
			// is explicitly set to allow editing - only need to do it if not already readonly
			if (path) {
				node = cache.getPathNode(nodeStart, path, bForcePath, onStep);
			}

			// if path does not exist, then return unsuccessfully
			if (nodeStart && !node) {
				return false;
			}

			if (node && (node != nodeStart)) {
				// if new url, then push new context; readonly status can only change if path
				// exists and node is not same as nodeStart
				this.contexts.unshift({
					urlNode: xml.nodeGetURL(node),
					urlParent: false,
					idRelation: null,
					pushCount: 0,
					readOnly: bReadOnly
				});
			} else {
				// increment push count which indicates push was called without actually adding new context
				this.contexts[0].pushCount++;
			}

			return true;
		},

		pop: function() {
			// summary: remove context for last path applied
			if (this.contexts[0].pushCount) {
				this.contexts[0].pushCount--;
			} else if (this.contexts.length > 1) {
				this.contexts.shift();
			}
		},

		/**
		 * If url is valid, then mark it as unresolved and handle based on context properties; the assumption
		 * is that the cache has already been checked for the node and it is not found
		 * @param {string} url  
		 */
		testForUnresolvedUrl: function(url) {
			if (_.isString(url) && xml.isValidURL(url)) {
				this.pathUnreachable = true;
				this.cache.setUnresolved(url);

				if (this.throwExceptionOnUnresolvedNode) {
					throw new Error("Unresolved path");
				}
			}
		}
	});
});